// ─────────────────────────────────────────────────────────────────────────
// Crisis Advisor — general-purpose advisory chat.
//
// Opens from the dashboard. Not tied to any incident — answers
// "what do I do if X happens?" with structured, phased guidance from
// Claude via Bedrock InvokeModel (see backend/src/routes/advisor.ts
// + backend/src/prompts/crisisAdvisor.ts).
//
// The chat + reply design deliberately mirrors the SitRep incident chat
// (IncidentChat.tsx): agent replies render as plain left-aligned prose
// under a "CIMA Advisor" attribution line + send time (no avatar, no
// bubble), the operator's own messages sit in a quiet neutral bubble on
// the right, and the composer is the same card pill + primary send button.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { MarkdownText } from '../components/MarkdownText';
import {
  AdvisorMessage,
  AdvisorSuggestion,
  getAdvisorGreeting,
  sendAdvisorMessage,
} from '../api/advisor';

interface Props {
  onBack: () => void;
}

// Send-time formatter — matches the SitRep chat's agent timestamp.
function msgTime(ts?: string): string {
  if (!ts) return '';
  const t = new Date(ts);
  return isNaN(t.getTime())
    ? ''
    : t.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

// ── Typing indicator ──────────────────────────────────────────────────────
// Same live-state indicator the SitRep chat uses: three bouncing dots + a
// pulsing "Advisor is thinking…" label, so a 5–20s Bedrock wait reads as
// activity rather than a stuck UI.
function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 2px' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 9, height: 9, borderRadius: '50%',
              background: 'var(--primary)',
              animation: `advisorTypingBounce 1.1s ease-in-out ${i * 0.15}s infinite`,
              boxShadow: '0 0 6px rgba(59,130,246,0.35)',
            }}
          />
        ))}
      </div>
      <span style={{
        fontSize: 12, color: 'var(--muted)', fontStyle: 'italic',
        letterSpacing: 0.2, animation: 'advisorTypingLabel 1.6s ease-in-out infinite',
      }}>
        Advisor is thinking…
      </span>
      <style>{`
        @keyframes advisorTypingBounce {
          0%, 80%, 100% { transform: translateY(0);   opacity: 0.45; }
          40%           { transform: translateY(-4px); opacity: 1;    }
        }
        @keyframes advisorTypingLabel {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
      `}</style>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export function CrisisAdvisor({ onBack }: Props) {
  const [messages, setMessages]       = useState<AdvisorMessage[]>([]);
  const [greeting, setGreeting]       = useState<string>('');
  const [suggestions, setSuggestions] = useState<AdvisorSuggestion[]>([]);
  const [input, setInput]             = useState('');
  const [sending, setSending]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const scrollRef                     = useRef<HTMLDivElement>(null);
  const taRef                         = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    getAdvisorGreeting()
      .then((data) => {
        if (cancelled) return;
        setGreeting(data.greeting);
        setSuggestions(data.suggestions);
      })
      .catch(() => {
        if (cancelled) return;
        setGreeting("I'm the Crisis Shield Advisor. Tell me what's happening — I'll walk you through the next steps.");
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || sending) return;
    setError(null);
    const userMsg: AdvisorMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    if (taRef.current) taRef.current.style.height = 'auto';
    setSending(true);
    try {
      const { message } = await sendAdvisorMessage(next);
      setMessages([...next, message]);
    } catch (err: any) {
      setError(err.message || 'Failed to reach the advisor.');
      setMessages(next);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const showEmpty = messages.length === 0;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      flex: 1, minHeight: 0,
      background: 'var(--bg)',
      position: 'relative',
    }}>
      <style>{`
        .advisor-chip:hover { transform: translateY(-1px); border-color: var(--primary-border); background: var(--primary-faint); }
        .advisor-chip:active { transform: translateY(0); }
        /* Suppress the global textarea focus ring on the inner control and put
           the focus affordance on the rounded pill instead — matches the
           SitRep composer so we don't draw a hard blue box inside the pill. */
        [data-tour="advisor.input"] textarea:focus-visible { border-color: transparent; box-shadow: none; }
        [data-tour="advisor.input"]:focus-within { border-color: var(--primary-border) !important; box-shadow: 0 0 0 3px var(--primary-mid) !important; }
      `}</style>

      {/* ── Header ── */}
      <div data-tour="advisor.header" style={{
        display: 'flex', alignItems: 'center', gap: 13, flex: 'none',
        padding: '13px 28px',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}>
        <button onClick={onBack} aria-label="Back to dashboard" style={{
          background: 'transparent', border: 'none', color: 'var(--muted)',
          cursor: 'pointer', padding: 4, marginLeft: -4, display: 'inline-flex',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <span style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: 'var(--primary)',
          color: '#fff', display: 'grid', placeItems: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        </span>
        <div style={{ lineHeight: 1.25 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 16, color: 'var(--fg)', letterSpacing: '-0.01em' }}>
            CIMA Advisor
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', background: 'var(--success)',
              boxShadow: '0 0 7px var(--success)', animation: 'cimaCriticalDotPulse 1.8s infinite',
            }} />
            Online · responding live
          </div>
        </div>
      </div>

      {/* ── Messages ── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: '20px',
          display: 'flex', flexDirection: 'column', gap: 12,
          maxWidth: 820, width: '100%', margin: '0 auto',
        }}
      >
        {showEmpty && (
          <div style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 18,
            padding: '26px 26px 22px',
            boxShadow: 'var(--elev-1)',
            backgroundImage: 'none',
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '5px 12px', borderRadius: 999,
              background: 'var(--primary-dim)', color: 'var(--primary)',
              fontSize: 11, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase',
              marginBottom: 14,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: 'var(--primary)',
                animation: 'cimaCriticalDotPulse 1.8s infinite',
              }} />
              Crisis Advisor · ready
            </div>
            <h2 style={{
              margin: '0 0 8px', fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em',
              color: 'var(--fg)',
            }}>How can I help you right now?</h2>
            <p style={{
              margin: '0 0 18px', fontSize: 14.5, lineHeight: 1.65,
              color: 'var(--fg-soft)',
            }}>{greeting || 'Loading…'}</p>

            {suggestions.length > 0 && (
              <div data-tour="advisor.chips">
                <div style={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8,
                  color: 'var(--muted)', textTransform: 'uppercase',
                  marginBottom: 10,
                }}>Common scenarios</div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 8,
                }}>
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      className="advisor-chip"
                      onClick={() => send(s.prompt)}
                      disabled={sending}
                      style={{
                        textAlign: 'left',
                        cursor: sending ? 'not-allowed' : 'pointer',
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 12,
                        padding: '11px 13px',
                        fontFamily: 'inherit', fontSize: 13, color: 'var(--fg)',
                        display: 'flex', alignItems: 'center', gap: 10,
                        transition: 'border-color 0.15s, background 0.15s, transform 0.12s',
                        opacity: sending ? 0.55 : 1,
                      }}
                    >
                      <span style={{
                        width: 28, height: 28, borderRadius: 9,
                        background: 'var(--primary-mid)', color: 'var(--primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, flexShrink: 0,
                        fontWeight: 800,
                      }}>›</span>
                      <span style={{ flex: 1, fontWeight: 600, lineHeight: 1.35 }}>{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.22)',
            borderRadius: 12, padding: '11px 14px', fontSize: 13, color: '#991B1B',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>⚠</span>
            <span>{error}</span>
          </div>
        )}

        {messages.map((msg, i) => {
          const isUser = msg.role === 'user';

          if (isUser) {
            // Operator message — quiet neutral bubble on the right (matches SitRep).
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  maxWidth: '80%', minWidth: 0,
                  background: 'color-mix(in srgb, var(--fg) 8%, transparent)',
                  color: 'var(--fg)', borderRadius: 16, padding: '10px 15px',
                  fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {msg.content}
                </div>
              </div>
            );
          }

          // Advisor reply — plain left-aligned prose under an attribution line
          // + send time (no avatar, no bubble), same as the SitRep agent reply.
          const t = msgTime(msg.timestamp);
          return (
            <div key={i} style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', letterSpacing: 0.2 }}>CIMA Advisor</span>
                {t && <span style={{ fontSize: 11, color: 'var(--subtle)' }}>{t}</span>}
              </div>
              <MarkdownText>{msg.content}</MarkdownText>
            </div>
          );
        })}

        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <TypingDots />
          </div>
        )}
      </div>

      {/* ── Input — same card pill + primary send button as the SitRep composer ── */}
      <div style={{
        flex: 'none',
        background: 'var(--surface)', borderTop: '1px solid var(--border)',
        padding: '12px 16px',
      }}>
        <div style={{ maxWidth: 820, margin: '0 auto', width: '100%' }}>
          {/* Quick chips when there are messages — suggestions stay accessible */}
          {!showEmpty && suggestions.length > 0 && (
            <div style={{
              display: 'flex', gap: 6, flexWrap: 'wrap',
              marginBottom: 10, paddingLeft: 2,
            }}>
              {suggestions.slice(0, 4).map((s, i) => (
                <button
                  key={i}
                  className="advisor-chip"
                  onClick={() => send(s.prompt)}
                  disabled={sending}
                  style={{
                    background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 999, padding: '5px 12px',
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                    color: 'var(--fg-soft)', cursor: sending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    opacity: sending ? 0.6 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span style={{ color: 'var(--primary)', fontWeight: 800 }}>+</span> {s.label}
                </button>
              ))}
            </div>
          )}

          <div data-tour="advisor.input" style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: 'var(--card)', border: '1px solid var(--border-md)',
            borderRadius: 12, padding: '8px 12px',
            transition: 'box-shadow 0.15s, border-color 0.15s',
          }}>
            <textarea
              ref={taRef}
              rows={1}
              value={input}
              placeholder="Describe what's happening or ask a question…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              }}
              disabled={sending}
              style={{
                flex: 1, resize: 'none', border: 'none', outline: 'none',
                background: 'transparent', color: 'var(--fg)',
                fontFamily: 'inherit', fontSize: 14, lineHeight: 1.5,
                padding: 0, maxHeight: 120, overflowY: 'auto',
              }}
            />
            <button
              onClick={() => send(input)}
              disabled={sending || !input.trim()}
              aria-label="Send"
              style={{
                background: input.trim() && !sending ? 'var(--primary)' : 'var(--faint)',
                border: 'none', borderRadius: 8,
                width: 36, height: 36,
                cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.2s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 2L2 7l5 1.5L8.5 14 14 2z" fill={input.trim() && !sending ? 'var(--on-primary)' : '#4b5563'} />
              </svg>
            </button>
          </div>

          <div style={{
            textAlign: 'center', fontSize: 11, color: 'var(--subtle)',
            marginTop: 8, lineHeight: 1.5,
          }}>
            Advisor is for guidance only — to log a real incident, click <strong style={{ color: 'var(--fg-soft)' }}>Report Incident</strong> from the dashboard.
          </div>
        </div>
      </div>
    </div>
  );
}
